import { KeyValue } from '@angular/common';
import { Injectable, inject } from '@angular/core';
import { EntityService } from '@core/services/entity/entity.service';
import { GridColumn } from '@grid/abstractions';
import { TranslateService } from '@ngx-translate/core';
import { DropdownItem } from '@shared/abstraction';
import { SecurityStatus } from '@shared/enums/security-status';
import { TaxResultItemType } from '@shared/enums/tax-result-item-type';
import { DataType } from '@shared/helpers/enums/data-type';
import { TransactionType } from '@shared/helpers/enums/transaction-type';
import { ActiveFilters } from '@shared/modules/grid/abstractions/active-filters';
import { ODataFilter } from '@shared/modules/grid/abstractions/odata-filter-column';
import { FilterTypes } from '@shared/modules/prime-grid-filters/helpers/enums/filter-type.enum';
import { asyncFilterOptionsFn } from '@shared/modules/prime-grid-filters/helpers/functions/async-filter-option';
import { CurrentUserService } from '@shared/services';
import { SecurityFilterRequest } from 'src/app/web-api-client';
import {
  Collection,
  EntityType,
  InvestmentJurisdiction,
  SecurityType,
} from '../../admin/models';
import {
  EntityTypeService,
  InvestmentJurisdictionService,
  SecurityTypeService,
} from '../../admin/services';
import { SecurityStatusGridIconComponent } from '../components/security-status-grid-icon/security-status-grid-icon.component';
import { Entity } from '../models/entity';
import { PortfolioService } from './portfolio.service';

@Injectable()
export class PortfolioHeadersService {
  private readonly translateService = inject(TranslateService);
  private readonly entityTypeService = inject(EntityTypeService);
  private readonly entityService = inject(EntityService);
  private readonly securityTypeService = inject(SecurityTypeService);
  private readonly investmentJurisdictionService = inject(
    InvestmentJurisdictionService
  );
  readonly portfolioService = inject(PortfolioService);
  private readonly currentUserService = inject(CurrentUserService);

  securityFilters: SecurityFilterRequest = new SecurityFilterRequest();

  clearSecurityFilters(): void {
    this.securityFilters = new SecurityFilterRequest();
  }

  headers(): GridColumn[] {
    return [
      {
        label: this.translateService.instant(
          'portfolio.securities.investingEntity'
        ),
        key: 'investingEntityName',
        sortKey: 'entity.name',
        dataType: DataType.String,
        isSortable: true,
        isFilterable: true,
        filterableColumn: {
          type: FilterTypes.Combobox,
          oDataPath: 'entity/name',
          options: {
            isMultiple: true,
            allowSearch: true,
            items: asyncFilterOptionsFn(
              this.entityService.entitiesByClientOrDefault,
              (entities: Entity[]) => {
                return entities.map((entity: Entity) => {
                  return {
                    value: `${entity.entityId?.toString() ?? ''}/name/${entity.name?.toString() ?? ''}`,
                    key: entity.name
                      ? this.currentUserService.isAdmin()
                        ? `${entity.name.toString()} - ${entity.clientName}`
                        : entity.name.toString()
                      : '',
                  } as KeyValue<string, string>;
                });
              }
            ),
          },
          oDataQueryBuilder: (columnFilter: ODataFilter): void => {
            columnFilter.updateQuery({
              in: columnFilter
                .getValue<KeyValue<string, string>[]>()
                .map(
                  (item: KeyValue<string, string>) =>
                    item.value.split('/name/')[1]
                ),
            });
          },
        },
      },
      {
        label: this.translateService.instant('portfolio.securities.entityType'),
        key: 'entityTypeName',
        sortKey: 'entity.entityType.name',
        dataType: DataType.String,
        isSortable: true,
        isFilterable: true,
        isVisible: false,
        filterableColumn: {
          type: FilterTypes.Dropdown,
          oDataPath: 'entity/entityType/name',
          options: {
            isMultiple: true,
            items: asyncFilterOptionsFn(
              this.entityTypeService.get(),
              ({ items }: Collection<EntityType>) => {
                return items.map((entityType: EntityType) => {
                  return {
                    value: entityType.entityTypeId.toString()
                      ? entityType.entityTypeId
                      : '',
                    key: entityType.name ? entityType.name.toString() : '',
                  } as KeyValue<string, string>;
                });
              }
            ),
          },
          oDataQueryBuilder: (columnFilter: ODataFilter): void => {
            columnFilter.updateQuery({
              in: columnFilter
                .getValue<KeyValue<string, string>[]>()
                .map((item: KeyValue<string, string>) => item.key),
            });
          },
        },
      },
      {
        label: this.translateService.instant('portfolio.securities.isin'),
        key: 'isinCusip',
        dataType: DataType.String,
        isSortable: true,
      },
      {
        label: this.translateService.instant('portfolio.securities.security'),
        key: 'name',
        dataType: DataType.String,
        isSortable: true,
        isFilterable: true,
        filterableColumn: {
          type: FilterTypes.TextField,
          oDataQueryBuilder: (columnFilter: ODataFilter): void => {
            columnFilter.updateQuery({
              contains: columnFilter.getValue<string>(),
            });
          },
        },
      },
      {
        label: this.translateService.instant(
          'portfolio.securities.investmentJurisdiction'
        ),
        key: 'investingJurisdictionName',
        sortKey: 'investmentJurisdiction.country.name',
        dataType: DataType.String,
        isSortable: true,
        isFilterable: true,
        filterableColumn: {
          type: FilterTypes.Combobox,
          oDataPath: 'investmentJurisdiction/country/name',
          options: {
            isMultiple: true,
            allowSearch: true,
            items: asyncFilterOptionsFn(
              this.investmentJurisdictionService.countryList,
              ({ items }: Collection<InvestmentJurisdiction>) => {
                return items.map(
                  (investmentJurisdiction: InvestmentJurisdiction) => {
                    return {
                      value: investmentJurisdiction.investmentJurisdictionId
                        ? investmentJurisdiction.investmentJurisdictionId.toString()
                        : '',
                      key: investmentJurisdiction.name
                        ? investmentJurisdiction.name.toString()
                        : '',
                    } as KeyValue<string, string>;
                  }
                );
              }
            ),
          },
          oDataQueryBuilder: (columnFilter: ODataFilter): void => {
            columnFilter.updateQuery({
              in: columnFilter
                .getValue<KeyValue<string, string>[]>()
                .map((item: KeyValue<string, string>) => item.key),
            });
          },
        },
      },
      {
        label: this.translateService.instant(
          'portfolio.securities.securityType'
        ),
        key: 'securityTypeName',
        sortKey: 'securityType.name',
        dataType: DataType.String,
        isSortable: true,
        isFilterable: true,
        filterableColumn: {
          type: FilterTypes.Dropdown,
          oDataPath: 'securityType/name',
          options: {
            isMultiple: true,
            items: asyncFilterOptionsFn(
              this.securityTypeService.activeSecurityTypeList(),
              ({ items }: Collection<SecurityType>) => {
                return items.map((securityType: SecurityType) => {
                  return {
                    value: securityType.securityTypeId
                      ? securityType.securityTypeId.toString()
                      : '',
                    key: securityType.name ? securityType.name.toString() : '',
                  } as KeyValue<string, string>;
                });
              }
            ),
          },
          oDataQueryBuilder: (columnFilter: ODataFilter): void => {
            columnFilter.updateQuery({
              in: columnFilter
                .getValue<KeyValue<string, string>[]>()
                .map((item: KeyValue<string, string>) => item.key),
            });
          },
        },
      },
      {
        label: this.translateService.instant(
          'portfolio.securities.addAcquisition'
        ),
        key: 'acquisitionDate',
        dataType: DataType.Date,
        isSortable: true,
        isFilterable: true,
        filterableColumn: {
          type: FilterTypes.Datepicker,
          oDataQueryBuilder: (columnFilter: ODataFilter): void => {
            columnFilter.updateQuery({
              ge: columnFilter.getValue<string[]>()[0],
              le: columnFilter.getValue<string[]>()[1],
            });
          },
          disableIf: () =>
            this.portfolioService.selectedTransactionType().value ===
            TransactionType.GeneralRates,
        },
      },
      {
        label: this.translateService.instant(
          'portfolio.securities.dispositionDate'
        ),
        key: 'dispositionDate',
        dataType: DataType.Date,
        isSortable: true,
        isFilterable: true,
        filterableColumn: {
          type: FilterTypes.Datepicker,
          oDataQueryBuilder: (columnFilter: ODataFilter): void => {
            columnFilter.updateQuery({
              ge: columnFilter.getValue<string[]>()[0],
              le: columnFilter.getValue<string[]>()[1],
            });
          },
        },
      },
      {
        label: this.translateService.instant('portfolio.withholdingTax'),
        key: 'dividendsRate',
        dataType: DataType.String,
        isSortable: false,
        isFilterable: true,
        filterableColumn: {
          type: FilterTypes.Slider,
          oDataPath: 'securityTaxResultItems',
          options: {
            min: 0,
            max: 100,
          },
          order: -1,
          oDataQueryBuilder: (columnFilter: ODataFilter): void => {
            columnFilter.updateQuery({
              any: {
                and: [
                  {
                    rate: {
                      ge: columnFilter.getValue<number[]>()[0],
                      le: columnFilter.getValue<number[]>()[1],
                    },
                    taxResultItemType: {
                      taxResultItemTypeId: {
                        in: [
                          TaxResultItemType.Dividends,
                          TaxResultItemType.Interest,
                        ],
                      },
                    },
                  },
                ],
              },
            });
          },
          disableIf: (activeFilters: ActiveFilters): boolean => {
            const filter: DropdownItem[] | undefined = activeFilters.get<
              DropdownItem[]
            >('securityStatus.name');
            return !!(
              filter && filter[0].value !== SecurityStatus.Approved.toString()
            );
          },
        },
      },
      {
        label: this.translateService.instant('portfolio.capitalGains'),
        key: 'capitalGainsRate',
        dataType: DataType.String,
        isSortable: false,
        isFilterable: true,
        filterableColumn: {
          type: FilterTypes.Slider,
          oDataPath: 'securityTaxResultItems',
          options: {
            max: 100,
            min: 0,
          },
          order: -2,
          oDataQueryBuilder: (columnFilter: ODataFilter): void => {
            columnFilter.updateQuery({
              any: {
                and: [
                  {
                    rate: {
                      ge: columnFilter.getValue<number[]>()[0],
                      le: columnFilter.getValue<number[]>()[1],
                    },
                    taxResultItemType: {
                      taxResultItemTypeId: {
                        in: [TaxResultItemType.CapitalGains],
                      },
                    },
                  },
                ],
              },
            });
          },
          disableIf: (activeFilters: ActiveFilters): boolean => {
            const filter: DropdownItem[] | undefined = activeFilters.get<
              DropdownItem[]
            >('securityStatus.name');
            return !!(
              filter && filter[0].value !== SecurityStatus.Approved.toString()
            );
          },
        },
      },
      {
        label: 'Status',
        key: 'securityStatus.name',
        dataType: DataType.Template,
        isFilterable: true,
        templateCell: SecurityStatusGridIconComponent,
        filterableColumn: {
          type: FilterTypes.Dropdown,
          oDataPath: 'securityStatus/name',
          oDataQueryBuilder: (columnFilter: ODataFilter): void => {
            columnFilter.updateQuery({
              in: columnFilter
                .getValue<KeyValue<string, string>[]>()
                .map((item: KeyValue<string, string>) => item.key),
            });
          },
          options: {
            items: [
              {
                value: SecurityStatus.Approved.toString(),
                key: this.translateService.instant('portfolio.approved'),
              },
              {
                value: SecurityStatus.InformationRequired.toString(),
                key: this.translateService.instant(
                  'portfolio.informationRequired'
                ),
              },
              {
                value: SecurityStatus.PendingApproval.toString(),
                key: this.translateService.instant('portfolio.pendingApproval'),
              },
            ],
          },
        },
      },
    ];
  }

  buildFilterString(filters?: ActiveFilters): string {
    let filterName = '';
    let filterString = '';
    this.securityFilters = new SecurityFilterRequest();
    if (this.portfolioService.selectedTransactionType().value) {
      const item = this.portfolioService.selectedTransactionType();
      this.securityFilters.transactionTypeId = parseInt(
        this.portfolioService.selectedTransactionType().value.toString()
      );
      filterName = this.translateService.instant(
        'portfolio.securities.transactionType'
      );
      filterString = filterString.concat(`${filterName}: ${item.label}, `);
    }
    if (filters?.get('investingEntityName')) {
      const item = filters?.get<KeyValue<string, string>[]>(
        'investingEntityName'
      )![0];
      this.securityFilters.entityId = parseInt(item.value.split('/name/')[0]);
      filterName = this.translateService.instant('portfolio.securities.entity');
      filterString = filterString.concat(`${filterName}: ${item.key}, `);
    }
    if (filters?.get('entityTypeName')) {
      const item =
        filters?.get<KeyValue<string, string>[]>('entityTypeName')![0];
      this.securityFilters.entityTypeId = parseInt(item.value);
      filterName = this.translateService.instant(
        'portfolio.securities.entityType'
      );
      filterString = filterString.concat(`${filterName}: ${item.key}, `);
    }
    if (filters?.get('name')) {
      const item = filters?.get<string>('name');
      this.securityFilters.nameDescription = item;
      filterName = this.translateService.instant('portfolio.securities.name');
      filterString = filterString.concat(`${filterName}: ${item}, `);
    }
    if (filters?.get('investingJurisdictionName')) {
      const item = filters?.get<KeyValue<string, string>[]>(
        'investingJurisdictionName'
      )![0];
      this.securityFilters.investmentJurisdictionId = parseInt(item.value);
      filterName = this.translateService.instant(
        'portfolio.securities.investmentJurisdiction'
      );
      filterString = filterString.concat(`${filterName}: ${item.key}, `);
    }
    if (filters?.get('securityTypeName')) {
      const item =
        filters?.get<KeyValue<string, string>[]>('securityTypeName')![0];
      this.securityFilters.securityTypeId = parseInt(item.value);
      filterName = this.translateService.instant(
        'portfolio.securities.securityType'
      );
      filterString = filterString.concat(`${filterName}: ${item.key}, `);
    }
    if (filters?.get('acquisitionDate')) {
      const item = filters?.get<Date[]>('acquisitionDate');
      this.securityFilters.acquisitionDateFrom = new Date(item![0]);
      this.securityFilters.acquisitionDateTo = new Date(item![1]);
      filterName = this.translateService.instant(
        'portfolio.securities.addAcquisition'
      );
      filterString = filterString.concat(
        `${filterName}: ${new Date(item![0]).toLocaleDateString()} - ${new Date(item![1]).toLocaleDateString()}, `
      );
    }
    if (filters?.get('dispositionDate')) {
      const item = filters?.get<Date[]>('dispositionDate');
      this.securityFilters.dispositionDateFrom = new Date(item![0]);
      this.securityFilters.dispositionDateTo = new Date(item![1]);
      filterName = this.translateService.instant(
        'portfolio.securities.dispositionDate'
      );
      filterString = filterString.concat(
        `${filterName}: ${new Date(item![0]).toLocaleDateString()} - ${new Date(item![1]).toLocaleDateString()}, `
      );
    }
    if (filters?.get('dividendsRate')) {
      const item = filters?.get<number[]>('dividendsRate');
      this.securityFilters.withholdingTaxFrom = item![0];
      this.securityFilters.withholdingTaxTo = item![1];
      filterName = this.translateService.instant('portfolio.withholdingTax');
      filterString = filterString.concat(
        `${filterName}: ${item![0]} - ${item![1]}, `
      );
    }
    if (filters?.get('capitalGainsRate')) {
      const item = filters?.get<number[]>('capitalGainsRate');
      this.securityFilters.capitalGainsFrom = item![0];
      this.securityFilters.capitalGainsTo = item![1];
      filterName = this.translateService.instant('portfolio.capitalGains');
      filterString = filterString.concat(
        `${filterName}: ${item![0]} - ${item![1]}, `
      );
    }
    if (filters?.get('securityStatus.name')) {
      const item = filters?.get<KeyValue<string, string>[]>(
        'securityStatus.name'
      )![0];
      this.securityFilters.securityStatusId = parseInt(item.value);
      filterName = this.translateService.instant('portfolio.status');
      filterString = filterString.concat(`${filterName}: ${item.key}, `);
    }
    return filterString.slice(0, -2);
  }
}
